import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import client from "../lib/api";
import { Panel } from "../components/ui-kit";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MediaPicker } from "../components/MediaPicker";

const inputCls = "w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none";
const lblCls = "block text-xs text-slate-400 mb-1";

export default function FollowUp() {
  const [text, setText] = useState("");
  const [instrument, setInstrument] = useState("Gold");
  const [media, setMedia] = useState(null);
  const [withButtons, setWithButtons] = useState(true);
  const [targets, setTargets] = useState({ free: true, paid: true, scalper: false });
  const [wa, setWa] = useState({ free: false, premium: false });
  const [pushApp, setPushApp] = useState(false);
  const [running, setRunning] = useState([]);
  const [linkId, setLinkId] = useState("");
  const [updatePips, setUpdatePips] = useState("");
  const [closeTrade, setCloseTrade] = useState(false);
  const [outcome, setOutcome] = useState("win");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    client.get("/vip-trades?status=running").then(({ data }) => setRunning(data)).catch(() => {});
  }, []);

  const tgt = (k, v) => setTargets((s) => ({ ...s, [k]: v }));

  const send = async () => {
    if (!targets.free && !targets.paid && !targets.scalper && !wa.free && !wa.premium) { toast.error("Select at least one channel (Telegram or WhatsApp)"); return; }
    if (!text.trim() && !media) { toast.error("Type a message or add an image/video"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify({
        text, instrument, targets, with_buttons: withButtons,
        wa_free: wa.free, wa_premium: wa.premium, push_app: pushApp,
        link_trade_id: linkId || null,
        update_pips: updatePips === "" ? null : Number(updatePips),
        close_trade: closeTrade, outcome,
      }));
      if (media) fd.append("media", media);
      const { data } = await client.post("/signals/follow-send", fd);
      const parts = Object.entries(data.results || {}).map(([k, v]) => `${k}: ${v.ok ? "✓" : "✗ " + (v.error || "")}`);
      if (data.ok) {
        toast.success("Follow-up sent — " + parts.join("  ·  ") + (data.trade_update ? ` · trade ${data.trade_update}` : ""));
        setText(""); setMedia(null); setUpdatePips(""); setCloseTrade(false);
        client.get("/vip-trades?status=running").then(({ data }) => setRunning(data)).catch(() => {});
      } else toast.error("Send failed — " + parts.join("  ·  "));
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    setSending(false);
  };

  return (
    <Layout title="Follow-up Message">
      <p className="text-sm text-slate-400 mb-4">Send a trade update or any message (with optional image/video) to your channels. Optionally link it to a running VIP trade to update its pips / close it.</p>
      <div className="max-w-2xl">
        <Panel title="Compose follow-up" testid="fu-form">
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1"><label className={lblCls}>Instrument</label>
                <select value={instrument} onChange={(e) => setInstrument(e.target.value)} data-testid="fu-instrument" className={inputCls}>
                  <option value="Gold">Gold</option><option value="BTC">BTC</option>
                </select>
              </div>
              <div className="col-span-2 flex items-end">
                <label className="flex items-center gap-2 text-sm text-slate-300 pb-2"><input type="checkbox" checked={withButtons} onChange={(e) => setWithButtons(e.target.checked)} data-testid="fu-buttons" /> Add Open Account / Contact buttons</label>
              </div>
            </div>

            <div><label className={lblCls}>Message</label>
              <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="fu-text" rows={4} className={inputCls} placeholder="e.g. TP1 hit ✅ +30 pips booked. Move SL to entry, trade is now risk-free." />
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Attachment (optional)</div>
              <MediaPicker value={media} onChange={setMedia} testid="fu-media" />
            </div>

            <div className="border-t border-[color:var(--border-subtle)] pt-3 space-y-3">
              <div className="text-xs uppercase tracking-wider text-slate-500">Link to a running trade (optional)</div>
              <select value={linkId} onChange={(e) => setLinkId(e.target.value)} data-testid="fu-link-trade" className={inputCls}>
                <option value="">— Don't link —</option>
                {running.map((t) => (
                  <option key={t.id} value={t.id}>{`${t.icon} ${t.instrument}${t.direction ? " " + t.direction : ""} · running ${t.running_pips ?? 0} pips`}</option>
                ))}
              </select>
              {linkId && (
                <div className="flex flex-wrap items-center gap-3">
                  <div><label className={lblCls}>Update pips</label>
                    <input type="number" value={updatePips} onChange={(e) => setUpdatePips(e.target.value)} data-testid="fu-update-pips" className={`${inputCls} w-32`} placeholder="e.g. 45" />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-300 pt-4"><input type="checkbox" checked={closeTrade} onChange={(e) => setCloseTrade(e.target.checked)} data-testid="fu-close" /> Close trade</label>
                  {closeTrade && (
                    <div className="pt-4"><select value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="fu-outcome" className={`${inputCls} w-28`}>
                      <option value="win">Win ✅</option><option value="loss">Loss ❌</option>
                    </select></div>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-[color:var(--border-subtle)] pt-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Send to</div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={targets.free} onChange={(e) => tgt("free", e.target.checked)} data-testid="fu-target-free" /> Free</label>
                <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={targets.paid} onChange={(e) => tgt("paid", e.target.checked)} data-testid="fu-target-paid" /> Paid (VIP)</label>
                <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={targets.scalper} onChange={(e) => tgt("scalper", e.target.checked)} data-testid="fu-target-scalper" /> Scalper</label>
              </div>
              <div className="pt-3 mt-3 border-t border-[color:var(--border-subtle)]">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#25D366]" /> Also send to WhatsApp</div>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={wa.free} onChange={(e) => setWa((s) => ({ ...s, free: e.target.checked }))} data-testid="fu-wa-free" /> WhatsApp Free</label>
                  <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={wa.premium} onChange={(e) => setWa((s) => ({ ...s, premium: e.target.checked }))} data-testid="fu-wa-premium" /> WhatsApp Premium</label>
                  <label className="flex items-center gap-2 text-sm text-gold"><input type="checkbox" checked={pushApp} onChange={(e) => setPushApp(e.target.checked)} data-testid="fu-push-app" /> 📱 App notification</label>
                </div>
              </div>
            </div>

            <button onClick={send} disabled={sending} data-testid="fu-send" className="btn-gold w-full py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {sending ? "Sending…" : "Send Follow-up"}
            </button>
          </div>
        </Panel>
      </div>
    </Layout>
  );
}
